/**
 * Pipeline Stage Detail Page
 * 
 * Displays comprehensive execution-grade details for a single pipeline stage:
 * - Basic info (Owner, SLA, Description)
 * - Entry/Exit Criteria
 * - Actions & Automations
 * - KPIs
 * - Mandatory CRM Fields
 * - Validation Rules
 * - Reason Codes
 * - Handover Rules
 * - Exception Playbooks
 * - Templates
 * - Activities (e.g., Test Drive in Showroom Engagement)
 * 
 * All data is driven by the consolidated Markdown registry.
 */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, 
  ArrowRight,
  Clock, 
  User, 
  CheckCircle, 
  XCircle, 
  Zap, 
  BarChart3, 
  FileText,
  AlertTriangle,
  MessageSquare,
  ArrowRightLeft,
  ClipboardList,
  Settings,
  Target,
  Activity
} from 'lucide-react';
import { BrandSelector } from '../../components/pipeline/BrandSelector';
import { getStageById, getAllStages } from '../../lib/leadPipelineRegistry';
import { getSOPsForStage } from '../../lib/sopsRegistry';
import type { 
  PipelineBrand, 
  PipelineStageRegistry
} from '../../types';

const PipelineStage: React.FC = () => {
  const { stageId } = useParams<{ stageId: string }>();
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const [selectedBrand, setSelectedBrand] = useState<PipelineBrand>('Changan');
  const [activeTab, setActiveTab] = useState<string>('overview');

  // Get stage data
  const stage = stageId ? getStageById(stageId) : undefined;
  const allStages = getAllStages();
  
  if (!stage) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {isArabic ? 'المرحلة غير موجودة' : 'Stage Not Found'}
        </h1>
        <Link 
          to="/pipeline" 
          className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {isArabic ? 'العودة إلى المسار' : 'Back to Pipeline'}
        </Link>
      </div>
    );
  }

  const { meta, description, descriptionAr } = stage;
  const isPreStage = meta.type === 'pre-stage';
  
  // Navigation
  const currentIndex = allStages.findIndex(s => s.meta.id === stageId);
  const prevStage = currentIndex > 0 ? allStages[currentIndex - 1] : null;
  const nextStage = currentIndex < allStages.length - 1 ? allStages[currentIndex + 1] : null;

  // Tabs configuration
  const tabs = [
    { id: 'overview', label: isArabic ? 'نظرة عامة' : 'Overview', icon: Target },
    { id: 'criteria', label: isArabic ? 'معايير الدخول/الخروج' : 'Entry/Exit Criteria', icon: CheckCircle },
    { id: 'actions', label: isArabic ? 'الإجراءات' : 'Actions & Automations', icon: Zap },
    { id: 'fields', label: isArabic ? 'حقول CRM' : 'CRM Fields', icon: FileText },
    { id: 'rules', label: isArabic ? 'القواعد' : 'Rules & Codes', icon: ClipboardList },
    { id: 'exceptions', label: isArabic ? 'الاستثناءات' : 'Exceptions', icon: AlertTriangle },
    { id: 'templates', label: isArabic ? 'القوالب' : 'Templates', icon: MessageSquare },
  ];

  // Add Activities tab if stage has activities
  if (stage.activities && stage.activities.length > 0) {
    tabs.splice(3, 0, { 
      id: 'activities', 
      label: isArabic ? 'الأنشطة' : 'Activities', 
      icon: Activity 
    });
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb & Navigation */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3 text-sm">
          <Link 
            to="/pipeline" 
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
          >
            <ArrowLeft className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
            {isArabic ? 'المسار' : 'Pipeline'}
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 dark:text-white font-medium">
            {isPreStage 
              ? (isArabic ? 'مرحلة النظام' : 'Pre-Stage')
              : `${isArabic ? 'المرحلة' : 'Stage'} ${meta.order}`
            }
          </span>
        </div>
        
        <BrandSelector 
          selectedBrand={selectedBrand} 
          onBrandChange={setSelectedBrand}
        />
      </div>

      {/* Stage Header */}
      <div 
        className="rounded-xl p-6 text-white"
        style={{ 
          background: `linear-gradient(135deg, ${meta.color}, ${adjustColor(meta.color, -20)})` 
        }}
      >
        <div className="flex items-start gap-4">
          {/* Stage Number */}
          <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-white/20 text-2xl font-bold shrink-0">
            {meta.order}
          </div>
          
          <div className="flex-1">
            {/* Type Badge */}
            <div className="flex items-center gap-2 mb-2">
              {isPreStage ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-white/20">
                  <Settings className="w-3 h-3" />
                  {isArabic ? 'مرحلة النظام' : 'System Pre-Stage'}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-white/20">
                  <Target className="w-3 h-3" />
                  {isArabic ? 'مرحلة CRM' : 'CRM Stage'}
                </span>
              )}
            </div>
            
            {/* Title */}
            <h1 className="text-2xl md:text-3xl font-bold mb-2">
              {isArabic ? meta.titleAr : meta.title}
            </h1>
            
            {/* Description */}
            <p className="text-white/90 mb-4 max-w-3xl">
              {isArabic ? descriptionAr : description}
            </p>
            
            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
                <User className="w-4 h-4" />
                <span>{isArabic ? meta.ownerAr : meta.owner}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
                <Clock className="w-4 h-4" />
                <span>{isArabic ? meta.slaAr : meta.sla}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Stage Navigation */}
        <div className="flex justify-between mt-6 pt-4 border-t border-white/20">
          {prevStage ? (
            <Link 
              to={`/pipeline/stage/${prevStage.meta.id}`}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
              <span className="text-sm">
                {isArabic ? prevStage.meta.titleAr : prevStage.meta.title}
              </span>
            </Link>
          ) : <div />}
          
          {nextStage ? (
            <Link 
              to={`/pipeline/stage/${nextStage.meta.id}`}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <span className="text-sm">
                {isArabic ? nextStage.meta.titleAr : nextStage.meta.title}
              </span>
              <ArrowRight className={`w-4 h-4 ${isArabic ? 'rotate-180' : ''}`} />
            </Link>
          ) : <div />}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-1 overflow-x-auto pb-px">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors
                  border-b-2 -mb-px
                  ${activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        {activeTab === 'overview' && <OverviewTab stage={stage} isArabic={isArabic} />}
        {activeTab === 'criteria' && <CriteriaTab stage={stage} isArabic={isArabic} />}
        {activeTab === 'actions' && <ActionsTab stage={stage} isArabic={isArabic} />}
        {activeTab === 'activities' && <ActivitiesTab stage={stage} isArabic={isArabic} />}
        {activeTab === 'fields' && <FieldsTab stage={stage} isArabic={isArabic} />}
        {activeTab === 'rules' && <RulesTab stage={stage} isArabic={isArabic} />}
        {activeTab === 'exceptions' && <ExceptionsTab stage={stage} isArabic={isArabic} />}
        {activeTab === 'templates' && <TemplatesTab stage={stage} isArabic={isArabic} />}
      </div>
    </div>
  );
};

// ============================================================================
// Tab Components
// ============================================================================

const OverviewTab: React.FC<{ stage: PipelineStageRegistry; isArabic: boolean }> = ({ 
  stage, 
  isArabic 
}) => {
  const { kpis, mandatoryFields, templates, exceptionPlaybooks } = stage;
  const relatedSOPs = getSOPsForStage(stage.meta.id);
  
  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard 
          label={isArabic ? 'مؤشرات الأداء' : 'KPIs'} 
          value={kpis.length} 
          icon={<BarChart3 className="w-5 h-5 text-green-500" />}
        />
        <StatCard 
          label={isArabic ? 'حقول CRM' : 'CRM Fields'} 
          value={mandatoryFields.length} 
          icon={<FileText className="w-5 h-5 text-blue-500" />}
        />
        <StatCard 
          label={isArabic ? 'القوالب' : 'Templates'} 
          value={templates.length} 
          icon={<MessageSquare className="w-5 h-5 text-purple-500" />}
        />
        <StatCard 
          label={isArabic ? 'الاستثناءات' : 'Exceptions'} 
          value={exceptionPlaybooks.length} 
          icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
        />
        <StatCard 
          label={isArabic ? 'الإجراءات' : 'SOPs'} 
          value={relatedSOPs.length} 
          icon={<ClipboardList className="w-5 h-5 text-orange-500" />}
        />
      </div>
      
      {/* KPIs List */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-green-500" />
          {isArabic ? 'مؤشرات الأداء الرئيسية' : 'Key Performance Indicators'}
        </h3>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {kpis.map((kpi, idx) => (
            <li 
              key={idx}
              className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
            >
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 text-xs font-medium shrink-0">
                {idx + 1}
              </span>
              <span className="text-sm text-gray-700 dark:text-gray-300">{kpi}</span>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Related SOPs */}
      {relatedSOPs.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-orange-500" />
            {isArabic ? 'إجراءات التشغيل الموحدة' : 'Standard Operating Procedures'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {relatedSOPs.map((sop) => (
              <Link
                key={sop.meta.id}
                to={`/sops/${sop.meta.id}`}
                className="flex items-start gap-3 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors truncate">
                    {isArabic ? sop.meta.titleAr : sop.meta.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {isArabic ? sop.meta.shortDescAr : sop.meta.shortDesc}
                  </p>
                </div>
                <ArrowRight className={`w-4 h-4 text-gray-400 group-hover:text-orange-500 transition-colors flex-shrink-0 ${isArabic ? 'rotate-180' : ''}`} />
              </Link>
            ))}
          </div>
          <div className="mt-3 text-center">
            <Link
              to="/sops"
              className="text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium"
            >
              {isArabic ? 'عرض جميع الإجراءات ←' : 'View all SOPs →'}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

const CriteriaTab: React.FC<{ stage: PipelineStageRegistry; isArabic: boolean }> = ({ 
  stage, 
  isArabic 
}) => {
  const { entryCriteria, exitCriteria } = stage;
  
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Entry Criteria */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          {isArabic ? 'معايير الدخول' : 'Entry Criteria'}
        </h3>
        <ul className="space-y-2">
          {entryCriteria.map((criteria, idx) => (
            <li 
              key={idx}
              className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
            >
              <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
              {criteria}
            </li>
          ))}
        </ul>
      </div>
      
      {/* Exit Criteria */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <XCircle className="w-5 h-5 text-red-500" />
          {isArabic ? 'معايير الخروج' : 'Exit Criteria'}
        </h3>
        <ul className="space-y-2">
          {exitCriteria.map((criteria, idx) => (
            <li 
              key={idx}
              className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
            >
              <ArrowRight className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
              {criteria}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const ActionsTab: React.FC<{ stage: PipelineStageRegistry; isArabic: boolean }> = ({ 
  stage, 
  isArabic 
}) => {
  const { actions, automations } = stage;
  
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Actions */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-blue-500" />
          {isArabic ? 'الإجراءات' : 'Actions'}
        </h3>
        <ul className="space-y-2">
          {actions.map((action, idx) => (
            <li 
              key={idx}
              className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-gray-700 rounded"
            >
              <span className="w-5 h-5 flex items-center justify-center rounded bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs font-medium shrink-0">
                {idx + 1}
              </span>
              {action}
            </li>
          ))}
        </ul>
      </div>
      
      {/* Automations */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" />
          {isArabic ? 'الأتمتة' : 'Automations'}
        </h3>
        <ul className="space-y-2">
          {automations.map((automation, idx) => (
            <li 
              key={idx}
              className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 p-2 bg-amber-50 dark:bg-amber-900/20 rounded"
            >
              <Zap className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              {automation}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const ActivitiesTab: React.FC<{ stage: PipelineStageRegistry; isArabic: boolean }> = ({ 
  stage, 
  isArabic 
}) => {
  const { activities } = stage;
  
  if (!activities || activities.length === 0) {
    return (
      <p className="text-gray-500 dark:text-gray-400 text-center py-8">
        {isArabic ? 'لا توجد أنشطة لهذه المرحلة' : 'No activities for this stage'}
      </p>
    );
  }
  
  return (
    <div className="space-y-6">
      {activities.map((activity, idx) => (
        <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-500" />
            {isArabic ? activity.titleAr || activity.title : activity.title}
          </h3>
          
          {activity.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {isArabic ? activity.descriptionAr || activity.description : activity.description}
            </p>
          )}
          
          {activity.steps.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                      {isArabic ? 'الخطوة' : 'Step'}
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                      {isArabic ? 'المالك' : 'Owner'}
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                      {isArabic ? 'الإجراء' : 'Action'}
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                      {isArabic ? 'تحديث CRM' : 'CRM Update'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activity.steps.map((step, stepIdx) => (
                    <tr key={stepIdx} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{step.step}</td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{step.owner}</td>
                      <td className="py-2 px-3 text-gray-700 dark:text-gray-300">{step.action}</td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{step.crmUpdate || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const FieldsTab: React.FC<{ stage: PipelineStageRegistry; isArabic: boolean }> = ({ 
  stage, 
  isArabic 
}) => {
  const { mandatoryFields, validationRules } = stage;
  
  return (
    <div className="space-y-6">
      {/* Mandatory Fields Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-500" />
          {isArabic ? 'حقول CRM الإلزامية' : 'Mandatory CRM Fields'}
        </h3>
        
        {mandatoryFields.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                    {isArabic ? 'الحقل' : 'Field'}
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                    {isArabic ? 'النوع' : 'Type'}
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                    {isArabic ? 'مطلوب' : 'Required'}
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                    {isArabic ? 'التحقق' : 'Validation'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {mandatoryFields.map((field, idx) => (
                  <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-4 font-mono text-xs text-gray-900 dark:text-gray-100">
                      {field.field}
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {field.type}
                    </td>
                    <td className="py-3 px-4">
                      {field.required === true || field.required === 'Yes' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                          {isArabic ? 'نعم' : 'Yes'}
                        </span>
                      ) : field.required === 'System' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          {isArabic ? 'نظام' : 'System'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                          {typeof field.required === 'string' ? field.required : (isArabic ? 'لا' : 'No')}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400 text-xs">
                      {field.validation || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">
            {isArabic ? 'لا توجد حقول محددة لهذه المرحلة' : 'No specific fields for this stage'}
          </p>
        )}
      </div>
      
      {/* Validation Rules */}
      {validationRules.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            {isArabic ? 'قواعد التحقق' : 'Validation Rules'}
          </h3>
          <ul className="space-y-2">
            {validationRules.map((rule, idx) => (
              <li key={idx} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="font-medium text-gray-900 dark:text-white">{rule.rule}:</span>
                <span className="ml-2 text-gray-600 dark:text-gray-400">{rule.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const RulesTab: React.FC<{ stage: PipelineStageRegistry; isArabic: boolean }> = ({ 
  stage, 
  isArabic 
}) => {
  const { reasonCodes, handoverRules } = stage;
  
  return (
    <div className="space-y-6">
      {/* Reason Codes */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-purple-500" />
          {isArabic ? 'رموز الأسباب' : 'Reason Codes'}
        </h3>
        
        {Object.entries(reasonCodes).map(([category, codes]) => {
          if (!codes || codes.length === 0) return null;
          
          const categoryLabels: Record<string, { en: string; ar: string }> = {
            lost: { en: 'For Lost', ar: 'للخسارة' },
            nurture: { en: 'For Nurture', ar: 'للرعاية' },
            noShow: { en: 'For No-Show', ar: 'لعدم الحضور' },
            cancellation: { en: 'For Cancellation', ar: 'للإلغاء' },
            delivery: { en: 'For Delivery', ar: 'للتسليم' },
            postSale: { en: 'For Post-Sale', ar: 'لما بعد البيع' },
          };
          
          return (
            <div key={category} className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {isArabic 
                  ? categoryLabels[category]?.ar || category 
                  : categoryLabels[category]?.en || category
                }
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                        {isArabic ? 'الرمز' : 'Code'}
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                        {isArabic ? 'التسمية' : 'Label'}
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                        {isArabic ? 'التسمية (عربي)' : 'Label (Arabic)'}
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                        {isArabic ? 'متى يُستخدم' : 'When to Use'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {codes.map((code, idx) => (
                      <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 px-3 font-mono text-xs text-gray-900 dark:text-gray-100">
                          {code.code}
                        </td>
                        <td className="py-2 px-3 text-gray-700 dark:text-gray-300">{code.label}</td>
                        <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{code.labelAr}</td>
                        <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{code.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
        
        {Object.values(reasonCodes).every(codes => !codes || codes.length === 0) && (
          <p className="text-gray-500 dark:text-gray-400">
            {isArabic ? 'لا توجد رموز أسباب لهذه المرحلة' : 'No reason codes for this stage'}
          </p>
        )}
      </div>
      
      {/* Handover Rules */}
      {handoverRules.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-blue-500" />
            {isArabic ? 'قواعد التسليم' : 'Handover Rules'}
          </h3>
          <ul className="space-y-2">
            {handoverRules.map((rule, idx) => (
              <li key={idx} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-start gap-2">
                <ArrowRightLeft className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {rule.from} → {rule.to}:
                  </span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{rule.description}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ExceptionsTab: React.FC<{ stage: PipelineStageRegistry; isArabic: boolean }> = ({ 
  stage, 
  isArabic 
}) => {
  const { exceptionPlaybooks } = stage;
  
  if (exceptionPlaybooks.length === 0) {
    return (
      <p className="text-gray-500 dark:text-gray-400 text-center py-8">
        {isArabic ? 'لا توجد استثناءات لهذه المرحلة' : 'No exception playbooks for this stage'}
      </p>
    );
  }
  
  return (
    <div className="space-y-4">
      {exceptionPlaybooks.map((playbook, idx) => (
        <div key={idx} className="border border-amber-200 dark:border-amber-800 rounded-lg p-4 bg-amber-50 dark:bg-amber-900/20">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            {playbook.title}
          </h4>
          
          {playbook.trigger && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span className="font-medium">{isArabic ? 'المحفز: ' : 'Trigger: '}</span>
              {playbook.trigger}
            </p>
          )}
          
          {playbook.actions.length > 0 && (
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {isArabic ? 'الإجراءات:' : 'Actions:'}
              </span>
              <ul className="mt-1 space-y-1">
                {playbook.actions.map((action, actionIdx) => (
                  <li key={actionIdx} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const TemplatesTab: React.FC<{ stage: PipelineStageRegistry; isArabic: boolean }> = ({ 
  stage, 
  isArabic 
}) => {
  const { templates } = stage;
  
  if (templates.length === 0) {
    return (
      <p className="text-gray-500 dark:text-gray-400 text-center py-8">
        {isArabic ? 'لا توجد قوالب لهذه المرحلة' : 'No templates for this stage'}
      </p>
    );
  }
  
  return (
    <div className="space-y-4">
      {templates.map((template, idx) => (
        <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-purple-500" />
              <span className="font-medium text-gray-900 dark:text-white">{template.title}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`
                px-2 py-1 rounded text-xs font-medium
                ${template.channel === 'WhatsApp' 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : template.channel === 'SMS'
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300'
                }
              `}>
                {template.channel}
              </span>
              <span className={`
                px-2 py-1 rounded text-xs font-medium
                ${template.language === 'ar'
                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300'
                }
              `}>
                {template.language === 'ar' ? 'عربي' : 'English'}
              </span>
            </div>
          </div>
          <pre className="p-4 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans overflow-x-auto bg-white dark:bg-gray-800">
            {template.content}
          </pre>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Helper Components
// ============================================================================

const StatCard: React.FC<{ label: string; value: number; icon: React.ReactNode }> = ({ 
  label, 
  value, 
  icon 
}) => (
  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center gap-3">
    {icon}
    <div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  </div>
);

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Adjust hex color brightness
 */
function adjustColor(color: string, amount: number): string {
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export default PipelineStage;
